import { collection, getDoc, getDocs, serverTimestamp, updateDoc } from "firebase/firestore";
import {
  countAttendees,
  type Guest,
  type PlusOne,
  type Child,
} from "@amazing-wedding/shared-types";
import { db } from "@/lib/firebase";
import { weddingRef } from "@/features/seating/firestore-paths";

const backfillInFlight = new Set<string>();

function parseGuestDoc(id: string, data: Omit<Guest, "id">): Guest {
  return {
    id,
    ...data,
    plusOne: data.plusOne ?? null,
    children: data.children ?? [],
    plusOneAllowed: data.plusOneAllowed ?? false,
    rsvpToken: data.rsvpToken ?? null,
  };
}

async function loadAllGuests(weddingId: string): Promise<Guest[]> {
  const snap = await getDocs(collection(db, "weddings", weddingId, "guests"));
  return snap.docs.map((d) => parseGuestDoc(d.id, d.data() as Omit<Guest, "id">));
}

/**
 * Number of (non-deleted) guest rows — the "actual added guests" surfaced as
 * `wedding.guestCount`. Distinct from `attendeeCount`, which counts invitation
 * heads (guest + plus-one + children) for the free-tier limit.
 */
export function countGuests(guests: Guest[]): number {
  return guests.filter((g) => !g.deletedAt).length;
}

/**
 * Writes wedding.attendeeCount + wedding.guestCount when either differs from
 * the values derived from `guests`. Call after guest-list mutations when the
 * caller has the updated guest array. Both counters are reconciled together so
 * `guestCount` (read by the admin panel) never drifts away from reality.
 */
export async function syncAttendeeCount(weddingId: string, guests: Guest[]): Promise<void> {
  const attendeeCount = countAttendees(guests);
  const guestCount = countGuests(guests);

  const snap = await getDoc(weddingRef(weddingId));
  const data = snap.data();
  if (data?.attendeeCount === attendeeCount && data?.guestCount === guestCount) return;

  await updateDoc(weddingRef(weddingId), {
    attendeeCount,
    guestCount,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Reconcile stored attendeeCount with a live guest array. Safe to call on
 * every guests snapshot — skips the write when already in sync.
 */
export async function maybeBackfillAttendeeCount(
  weddingId: string,
  guests: Guest[],
): Promise<void> {
  if (backfillInFlight.has(weddingId)) return;

  backfillInFlight.add(weddingId);
  try {
    await syncAttendeeCount(weddingId, guests);
  } finally {
    backfillInFlight.delete(weddingId);
  }
}

/**
 * One-shot backfill for routes that never mount useGuests (e.g. Sidebar-only
 * views). Loads guests once when the wedding doc lacks attendeeCount.
 */
export async function ensureAttendeeCountBackfill(weddingId: string): Promise<void> {
  if (backfillInFlight.has(weddingId)) return;

  backfillInFlight.add(weddingId);
  try {
    const weddingSnap = await getDoc(weddingRef(weddingId));
    const stored = weddingSnap.data()?.attendeeCount as number | undefined;
    if (typeof stored === "number") return;

    const guests = await loadAllGuests(weddingId);
    await syncAttendeeCount(weddingId, guests);
  } finally {
    backfillInFlight.delete(weddingId);
  }
}

/** True when RSVP household fields can change invitation head count. */
export function rsvpSubmissionAffectsAttendeeCount(
  before: Pick<Guest, "plusOne" | "children">,
  submission: { plusOne: PlusOne | null; children: Child[] },
): boolean {
  return (
    JSON.stringify(before.plusOne ?? null) !== JSON.stringify(submission.plusOne ?? null) ||
    JSON.stringify(before.children ?? []) !== JSON.stringify(submission.children ?? [])
  );
}

/** Sync attendeeCount once after createGuest + optional legacy link projection. */
export async function syncAfterGuestCreate(
  weddingId: string,
  allGuests: Guest[],
  newId: string,
  draft: Pick<
    Guest,
    | "firstName"
    | "lastName"
    | "plusOne"
    | "children"
    | "plusOneAllowed"
    | "weddingRole"
    | "deletedAt"
  >,
  linked: Guest[] | null,
): Promise<void> {
  await syncAttendeeCount(weddingId, linked ?? [...allGuests, projectCreatedGuest(newId, draft)]);
}

/** Build a guest row for count projection after createGuest (before snapshot refresh). */
export function projectCreatedGuest(
  id: string,
  draft: Pick<
    Guest,
    | "firstName"
    | "lastName"
    | "plusOne"
    | "children"
    | "plusOneAllowed"
    | "weddingRole"
    | "deletedAt"
  >,
): Guest {
  return {
    id,
    firstName: draft.firstName,
    lastName: draft.lastName,
    plusOne: draft.plusOne ?? null,
    children: draft.children ?? [],
    plusOneAllowed: draft.plusOneAllowed ?? false,
    weddingRole: draft.weddingRole,
    deletedAt: draft.deletedAt ?? null,
    rsvpStatus: "pending",
    attendingCeremony: true,
    attendingReception: true,
    attendingAfterParty: false,
    accommodationRequired: false,
    rsvpToken: null,
    importSource: "manual",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/** Test helper — reset module-level backfill guards. */
export function resetAttendeeCountBackfillState(): void {
  backfillInFlight.clear();
}
